import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineCheckCircle, AiOutlineClockCircle } from "react-icons/ai";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { updateCaseStatus } from "../../store/common/updateCaseStatusSlice";
// import { fetchCaseById } from "../../store/user/userSlice";
import { useParams } from "react-router-dom";
import { useToast } from "../utilities/Toast";

const steps = [
  "files pending",
  "submitted",
  "query pending",
  "under process",
  "completed",
];
const labels = [
  "Files Pending",
  "Submitted",
  "Query Pending",
  "Under Process",
  "Completed",
];

const Status: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const { user } = useAppSelector((s) => s.auth);
  const { selectedCase } = useAppSelector((s) => s.adminCases);
  const { isLoading } = useAppSelector((s) => s.updateCaseStatus);
  const { case_id } = useParams<{ case_id: string }>();

  const [displayedStatus, setDisplayedStatus] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [customStatus, setCustomStatus] = useState<string>("");

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // useEffect(() => {
  //   if (case_id) dispatch(fetchCaseById(case_id));
  // }, [case_id, dispatch]);

  useEffect(() => {
    if (selectedCase?.status) {
      const normalized = selectedCase.status.trim().toLowerCase();
      setDisplayedStatus(normalized);
      setSelectedStatus(normalized);
    }
  }, [selectedCase]);

  const currentStep = useMemo(() => {
    const idx = steps.indexOf(displayedStatus);
    return idx === -1 ? 0 : idx;
  }, [displayedStatus]);

  const progressPercent = (currentStep / (steps.length - 1)) * 100;

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
    setCustomStatus("");
  };

  // Open confirmation modal instead of direct submit
  const handleSubmitClick = () => {
    if (!selectedStatus && !customStatus) return;
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    const statusToUpdate =
      selectedStatus === "others" ? customStatus.trim() : selectedStatus;
    if (!statusToUpdate) return;

    try {
      const res = await dispatch(
        updateCaseStatus({ case_id: case_id!, updatedStatus: statusToUpdate })
      ).unwrap();

      setDisplayedStatus(res.data);
      setSelectedStatus(res.data);
      setCustomStatus("");

      showToast(`Status updated to ${res.data}`, "success");
    } catch (err) {
      console.error("Failed to update status:", err);
      showToast("Failed to update status", "error");
    }
  };

  return (
    <div className="relative w-full px-1 py-2 bg-white rounded-2xl shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Case Status</h2>

        {user?.role?.toLowerCase() !== "admin read" &&
          user?.role?.toLowerCase() !== "production" &&
          user?.role?.toLowerCase() !== "side admin" && (
            <div className="flex gap-2">
              <select
                value={selectedStatus}
                onChange={handleSelectChange}
                className="border rounded px-2 py-1 text-sm shadow-sm cursor-pointer"
              >
                {steps.map((s, i) => (
                  <option
                    key={i}
                    value={s}
                    disabled={s === "files pending" || s === "submitted"}
                  >
                    {labels[i]}
                  </option>
                ))}
                <option value="others">Others</option>
              </select>

              {selectedStatus === "others" && (
                <input
                  type="text"
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  placeholder="Enter custom status"
                  className="border rounded px-2 py-1 text-sm w-48"
                />
              )}

              <button
                disabled={isLoading}
                onClick={handleSubmitClick}
                className="px-3 py-1 bg-[#2fa9bf] text-white rounded text-sm w-fit"
              >
                {isLoading ? "Updating..." : "Submit"}
              </button>
            </div>
          )}
      </div>

      {/* Progress Bar OR Status Text */}
      {steps.includes(displayedStatus) ? (
        <>
          {/* Progress Bar */}
          <div className="relative h-3 bg-gray-300 rounded-full mb-4">
            <div
              className="absolute h-full bg-[#2fa9bf] rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
            <div className="absolute top-1/2 left-0 w-full flex justify-between -translate-y-1/2">
              {steps.map((_, i) => {
                const isCompleted = i < currentStep;
                const isCurrent = i === currentStep;
                const circleClass = isCompleted
                  ? "bg-green-500 border-green-500 text-white"
                  : isCurrent
                  ? "bg-yellow-400 border-yellow-400 text-white"
                  : "bg-white border-gray-400 text-gray-400";
                return (
                  <div
                    key={i}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${circleClass}`}
                  >
                    {isCompleted ? (
                      <AiOutlineCheckCircle />
                    ) : (
                      <AiOutlineClockCircle />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Labels */}
          <div className="flex justify-between text-xs font-medium text-gray-500">
            {labels.map((label, i) => (
              <span
                key={i}
                className={i === currentStep ? "text-yellow-600" : ""}
              >
                {label}
              </span>
            ))}
          </div>
        </>
      ) : (
        // Render only the status name if it's not in steps
        <div className="text-sm font-medium text-gray-700 mb-4">
          {selectedCase?.status || displayedStatus}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">
              Confirm Status Update
            </h3>
            <p className="mb-6">
              Are you sure you want to update status to{" "}
              <span className="font-bold">
                {selectedStatus === "others" ? customStatus : selectedStatus}
              </span>
              ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#2fa9bf] text-white rounded"
                onClick={handleConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Status;
